import React from "react";
import { z } from "zod";
import { DndContext, closestCenter, DragOverlay } from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  horizontalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { FiChevronUp, FiChevronDown, FiMoreVertical, FiEyeOff, FiFilter } from "react-icons/fi";
import { MdCalculate } from "react-icons/md";
import Button from "../Button/Button";
import SelectField from "../SelectField/SelectField";
import clsx from "clsx";
import SmartImage from "../SmartImage/SmartImage";
import { FiChevronLeft, FiChevronRight, FiChevronsLeft, FiChevronsRight } from "react-icons/fi";
import Accordion from "../Accordion/Accordion";
import Menu from "../Menu/Menu";

// Zod schemas
const CustomColumnSchema = z.object({
  name: z.string(),
  expr: z.string(),
});
const ColumnsConfigSchema = z.object({
  key: z.string(),
  label: z.string(),
});

const TablePropsSchema = z.object({
  columns: z.array(z.string()),
  customColumns: z.array(CustomColumnSchema),
  sortedData: z.array(z.record(z.unknown())),
  columnWidths: z.array(z.string()),
  setColumnWidths: z.function().args(z.any()).returns(z.any()),
  sortCol: z.string().nullable(),
  sortDir: z.union([z.literal("asc"), z.literal("desc")]),
  toggleSort: z.function().args(z.string()).returns(z.void()),
  setMathMenuCol: z.function().args(z.string()).returns(z.void()),
  setFilterCol: z.function().args(z.string()).returns(z.void()),
  setFilterModalOpen: z.function().args(z.boolean()).returns(z.void()),
  isColFiltered: z.function().args(z.string()).returns(z.boolean()),
  onColumnOrderChange: z.function().args(z.array(z.string())).returns(z.void()),
  hoverCol: z.number().nullable(),
  setHoverCol: z
    .function()
    .args(z.union([z.number(), z.null()]))
    .returns(z.void()),
  formatValue: z.function().args(z.unknown()).returns(z.string()),
  getCellAlignment: z
    .function()
    .args(z.unknown())
    .returns(
      z.union([z.literal("left"), z.literal("right"), z.literal("center")])
    ),
  isRowFiltered: z.function().args(z.record(z.unknown())).returns(z.boolean()),
  mathSelections: z.record(z.string()).optional(),
  setMathSelections: z.function().args(z.any()).returns(z.any()).optional(),
  currentPage: z.number(),
  setCurrentPage: z.function().args(z.any()).returns(z.any()),
  pageSize: z.number(),
  totalPages: z.number(),
  setPageSize: z.function().args(z.number()).returns(z.any()).optional(),
  theme: z.union([z.literal("light"), z.literal("dark")]).default("light"),
  groupBy: z.string().nullable().optional(),
  columnsConfig: z.array(ColumnsConfigSchema).optional(),
  // Adicione esta linha:
  onHideColumn: z.function().args(z.string()).returns(z.void()),
});

type TableProps = z.infer<typeof TablePropsSchema>;

// Botão: utilitário para calcular largura dos botões de header
const BUTTON_SIZE = 28;
const GAP = 4;
function getButtonsWidth() {
  // Só existe o botão do menu agora
  return BUTTON_SIZE + GAP; // Adiciona o gap entre texto e botão
}

function Table(props: TableProps) {
  // Validação em tempo de execução (apenas em dev)
  if (process.env.NODE_ENV !== "production") {
    TablePropsSchema.parse(props);
  }

  const {
    columns,
    customColumns,
    sortedData,
    columnWidths: propColumnWidths,
    sortCol,
    sortDir,
    toggleSort,
    setMathMenuCol,
    onColumnOrderChange,
    hoverCol,
    setHoverCol,
    formatValue,
    getCellAlignment,
    isRowFiltered,
    currentPage,
    setCurrentPage,
    pageSize,
    totalPages,
    theme = "light",
    groupBy,
    onHideColumn,
  } = props;

  // NOVO: Mapeamento de labels se vierem via props.columnsConfig
  // Supondo que props.columnsConfig seja [{ key, label }]
  const columnLabels = React.useMemo(() => {
    if (props.columnsConfig) {
      return Object.fromEntries(
        props.columnsConfig.map((c: z.infer<typeof ColumnsConfigSchema>) => [c.key, c.label])
      );
    }
    return {};
  }, [props.columnsConfig]);

  const allCols = React.useMemo(
    () => [...columns, ...customColumns.map((c) => c.name)],
    [columns, customColumns]
  );

  // Calcule minWidths para cada coluna (igual ao SortableTh)
  const minWidths = React.useMemo(() => {
    return allCols.map((col) => {
      const label = columnLabels[col] || col;
      const titleText = label
        .replace(/_/g, " ")
        .replace(
          /\w\S*/g,
          (txt) => txt.charAt(0).toUpperCase() + txt.slice(1).toLowerCase()
        );
      const titleWidth = estimateTextWidth(titleText) + 8;
      const buttonsWidth = getButtonsWidth();
      return Math.ceil(titleWidth + buttonsWidth);
    });
  }, [allCols, columnLabels]);

  // Garante que cada coluna tenha pelo menos o minWidth calculado
  const [localColumnWidths, setLocalColumnWidths] = React.useState<string[]>(() =>
    (propColumnWidths && propColumnWidths.length === allCols.length
      ? propColumnWidths
      : minWidths.map(w => `${w}px`)
    )
  );

  React.useEffect(() => {
    // Se columns mudarem, reseta para minWidths
    setLocalColumnWidths(
      propColumnWidths && propColumnWidths.length === allCols.length
        ? propColumnWidths
        : minWidths.map(w => `${w}px`)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allCols.join(","), minWidths.join(","), propColumnWidths && propColumnWidths.join(",")]);

  // Drag & drop handlers (simplificado)
  const [activeId, setActiveId] = React.useState<string | null>(null);
  const [activeColIdx, setActiveColIdx] = React.useState<number | null>(null);
  const handleDragStart = (event: import("@dnd-kit/core").DragStartEvent) => {
    setActiveId(event.active.id as string);
    setActiveColIdx(allCols.indexOf(event.active.id as string));
  };
  const handleDragEnd = (event: import("@dnd-kit/core").DragEndEvent) => {
    setActiveId(null);
    setActiveColIdx(null);
    const { active, over } = event;
    if (active && over && active.id !== over.id) {
      const oldIndex = allCols.indexOf(String(active.id));
      const newIndex = allCols.indexOf(String(over.id));
      if (oldIndex !== -1 && newIndex !== -1) {
        const newOrder = arrayMove(allCols, oldIndex, newIndex);
        onColumnOrderChange(newOrder);
      }
    }
  };

  // Função para resize de coluna
  const handleResize = React.useCallback(
    (colIdx: number, delta: number) => {
      setLocalColumnWidths((prev: string[]) => {
        const match =
          typeof prev[colIdx] === "string"
            ? prev[colIdx].match(/^(\d+)/)
            : null;
        const px: number =
          match && match[1] && !isNaN(Number(match[1]))
            ? parseInt(match[1], 10)
            : 120;
        const newPx: number = Math.max(60, px + delta);
        const newWidths: string[] = [...prev];
        newWidths[colIdx] = `${newPx}px`;
        return newWidths;
      });
    },
    [setLocalColumnWidths]
  );

  // Footer helpers

  // PAGINAÇÃO DIRETO NO TABLE
  // Remova o estado local de pageSize e o cálculo de paginatedTotalPages:
  // const [localPageSize, setLocalPageSize] = React.useState(20);
  // const paginatedTotalPages = Math.ceil(sortedData.length / localPageSize);

  // Use as props pageSize e totalPages:
  // Atualiza currentPage se totalPages mudar
  React.useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages || 1);
  }, [totalPages, currentPage, setCurrentPage]);

  // Dados paginados usando pageSize da prop
  const paginatedData = React.useMemo(
    () =>
      sortedData.slice((currentPage - 1) * pageSize, currentPage * pageSize),
    [sortedData, currentPage, pageSize]
  );

  // Larguras de coluna iniciais (baseado em allCols)
  // (Removido initialColumnWidths pois não era utilizado)

  // Se groupBy, espera sortedData como [{ group, rows }]
  const isGrouped = !!groupBy && Array.isArray(sortedData) && sortedData[0]?.rows;

  // NOVO: Mapeamento de labels se vierem via props.columnsConfig
  // Supondo que props.columnsConfig seja [{ key, label }]
  // Novo: controla qual coluna está com menu aberto
  const [menuCol, setMenuCol] = React.useState<string | null>(null);
  const [menuAnchorEl, setMenuAnchorEl] = React.useState<React.RefObject<HTMLButtonElement> | null>(null);

  // Estado para linha ghost do resize
  const [resizeGhostX, setResizeGhostX] = React.useState<number | null>(null);

  return (
    <DndContext
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div className={clsx(
        "flex flex-col items-center w-full",
        theme === "dark" ? "dark" : ""
      )}>
        {/* Linha ghost do resize DENTRO da tabela */}
        <div
          className="w-auto max-w-screen overflow-x-auto rounded-xl relative" // <-- adicione 'relative' aqui
          style={{ maxHeight: 480, overflowY: "auto" }}
        >
          {/* Linha ghost do resize DENTRO da tabela */}
          {resizeGhostX !== null && (
            <div
              style={{
                position: "absolute",
                top: 0,
                left: resizeGhostX,
                height: "100%",
                width: 2,
                background: "#0078d4",
                zIndex: 99,
                pointerEvents: "none",
                transition: "none",
              }}
            />
          )}
          <table
            className={clsx(
              "border-separate border-spacing-0 rounded-xl text-[15px] shadow-sm min-w-max w-full",
              "table-fixed",
              theme === "dark" ? "bg-zinc-900" : "bg-white"
            )}
          >
            <colgroup>
              {localColumnWidths.map((w, i) => (
                <col key={i} style={{ width: w }} />
              ))}
            </colgroup>
            <SortableContext
              items={allCols}
              strategy={horizontalListSortingStrategy}
            >
              <thead>
                <tr className={clsx(
                  theme === "dark"
                    ? "bg-zinc-900 border-b border-zinc-700"
                    : "bg-[#f3f2f1] border-b border-gray-200"
                )}>
                  {allCols.map((col, colIdx) => (
                    <SortableTh
                      key={col}
                      col={col}
                      colIdx={colIdx}
                      sortCol={sortCol}
                      sortDir={sortDir}
                      hoverCol={hoverCol}
                      setHoverCol={setHoverCol}
                      toggleSort={toggleSort}
                      sortedData={sortedData}
                      setMathMenuCol={setMathMenuCol}
                      activeId={activeId}
                      handleResize={handleResize}
                      allColsLength={allCols.length}
                      theme={theme}
                      className={clsx(
                        colIdx === 0 &&
                          (theme === "dark"
                            ? "sticky left-0 z-20 bg-zinc-800"
                            : "sticky left-0 z-20 bg-[#f9fafb]")
                      )}
                      columnLabel={columnLabels[col] ?? undefined}
                      menuCol={menuCol}
                      setMenuCol={setMenuCol}
                      menuAnchorEl={menuAnchorEl}
                      setMenuAnchorEl={setMenuAnchorEl}
                      onHideColumn={onHideColumn}
                      setFilterCol={props.setFilterCol}
                      setFilterModalOpen={props.setFilterModalOpen}
                      resizeGhostX={resizeGhostX}
                      setResizeGhostX={setResizeGhostX}
                    />
                  ))}
                </tr>
              </thead>
              <tbody>
                {isGrouped ? (
                  <tr>
                    <td colSpan={allCols.length} className="p-0 border-none">
                      <Accordion theme={theme} className="w-full" multiOpen>
                        {sortedData.map(({ group, rows }, groupIdx) => (
                          <Accordion.Item key={groupIdx} index={groupIdx}>
                            <Accordion.Header index={groupIdx}>
                              {groupBy}: {String(group)}
                            </Accordion.Header>
                            <Accordion.Body index={groupIdx}>
                              <table className="w-full border-separate border-spacing-0">
                                <colgroup>
                                  {localColumnWidths.map((w, i) => (
                                    <col key={i} style={{ width: w }} />
                                  ))}
                                </colgroup>
                                <tbody>
                                  {(rows as Record<string, unknown>[]).map((rowData, index) => (
                                    <tr
                                      key={index}
                                      data-testid={`table-row-${groupIdx}-${index}`}
                                      tabIndex={0}
                                      className={clsx(
                                        "w-full group transition-colors duration-100 outline-none",
                                        theme === "dark"
                                          ? index % 2 === 0
                                            ? "bg-zinc-900"
                                            : "bg-zinc-800"
                                          : index % 2 === 0
                                            ? "bg-[#f3f2f1]"
                                            : "bg-white",
                                        isRowFiltered(rowData) &&
                                          (theme === "dark"
                                            ? "bg-red-900/30 hover:bg-red-900/40"
                                            : "bg-red-50 hover:bg-red-100"),
                                        "focus-visible:ring-2 focus-visible:ring-[#0078d4] focus:z-10"
                                      )}
                                      style={{
                                        borderBottom: theme === "dark" ? "1px solid #27272a" : "1px solid #eee",
                                        boxSizing: "border-box",
                                        minHeight: "44px",
                                      }}
                                    >
                                      {allCols.map((col, colIdx) => (
                                        <td
                                          key={col}
                                          data-testid={`table-cell-${groupIdx}-${index}-${col}`}
                                          className={clsx(
                                            "overflow-hidden break-words whitespace-normal px-4 py-3 text-[15px] font-normal border-none align-middle transition-colors duration-100 font-grotesk",
                                            `text-${getCellAlignment(rowData[col])}`,
                                            theme === "dark"
                                              ? "group-hover:bg-blue-900/20 text-zinc-100"
                                              : "group-hover:bg-[#e5f1fb] text-zinc-900",
                                            "focus-visible:outline-none",
                                            colIdx === 0 && "sticky left-0 z-10 bg-inherit"
                                          )}
                                          style={{
                                            wordBreak: "break-word",
                                            whiteSpace: "nowrap",
                                            lineHeight: "1.5",
                                            verticalAlign: "middle",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                          }}
                                        >
                                          {typeof rowData[col] === "string" &&
                                          rowData[col].match(
                                            /\.(jpe?g|png|gif|webp|bmp|svg)$/i
                                          ) ? (
                                            <SmartImage
                                              src={rowData[col]}
                                              alt={col}
                                              width={60}
                                              height={40}
                                              layout="fixed"
                                              style={{
                                                maxWidth: 80,
                                                maxHeight: 60,
                                                borderRadius: 6,
                                              }}
                                              showZoomButton
                                            />
                                          ) : (
                                            formatValue(rowData[col])
                                          )}
                                        </td>
                                      ))}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </Accordion.Body>
                          </Accordion.Item>
                        ))}
                      </Accordion>
                    </td>
                  </tr>
                ) : (
                  paginatedData.map((rowData, index) => (
                    <tr
                      key={index}
                      data-testid={`table-row-${index}`}
                      tabIndex={0}
                      className={clsx(
                        // Remova "grid" daqui!
                        "w-full group transition-colors duration-100 outline-none",
                        theme === "dark"
                          ? index % 2 === 0
                            ? "bg-zinc-900"
                            : "bg-zinc-800"
                          : index % 2 === 0
                            ? "bg-[#f3f2f1]"
                            : "bg-white",
                        isRowFiltered(rowData) &&
                          (theme === "dark"
                            ? "bg-red-900/30 hover:bg-red-900/40"
                            : "bg-red-50 hover:bg-red-100"),
                        "focus-visible:ring-2 focus-visible:ring-[#0078d4] focus:z-10"
                      )}
                      style={{
                        // Remova gridTemplateColumns daqui!
                        borderBottom: theme === "dark" ? "1px solid #27272a" : "1px solid #eee",
                        boxSizing: "border-box",
                        minHeight: "44px",
                      }}
                      onMouseEnter={(e) =>
                        e.currentTarget.classList.add("hovered")
                      }
                      onMouseLeave={(e) =>
                        e.currentTarget.classList.remove("hovered")
                      }
                    >
                      {allCols.map((col, colIdx) => (
                        <td
                          key={col}
                          data-testid={`table-cell-${index}-${col}`}
                          className={clsx(
                            "overflow-hidden break-words whitespace-normal px-4 py-3 text-[15px] font-normal border-none align-middle transition-colors duration-100 font-grotesk", // <-- aqui
                            `text-${getCellAlignment(rowData[col])}`,
                            theme === "dark"
                              ? "group-hover:bg-blue-900/20 text-zinc-100"
                              : "group-hover:bg-[#e5f1fb] text-zinc-900",
                            "focus-visible:outline-none",
                            colIdx === 0 && "sticky left-0 z-10 bg-inherit" // sticky para a primeira coluna
                          )}
                          style={{
                            wordBreak: "break-word",
                            whiteSpace: "nowrap",
                            lineHeight: "1.5",
                            verticalAlign: "middle",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {typeof rowData[col] === "string" &&
                          rowData[col].match(
                            /\.(jpe?g|png|gif|webp|bmp|svg)$/i
                          ) ? (
                            <SmartImage
                              src={rowData[col]}
                              alt={col}
                              width={60}
                              height={40}
                              layout="fixed"
                              style={{
                                maxWidth: 80,
                                maxHeight: 60,
                                borderRadius: 6,
                              }}
                              showZoomButton
                            />
                          ) : (
                            formatValue(rowData[col])
                          )}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </SortableContext>
          </table>
        </div>
        {/* CONTROLES DE PAGINAÇÃO - LINHA ÚNICA, CENTRALIZADO, GAP REDUZIDO */}
        <div className="w-full flex justify-center mt-2">
          <div
            className="flex items-center gap-2 px-2 py-1 rounded bg-transparent shadow-none whitespace-nowrap"
            style={{
              minWidth: 0,
              maxWidth: 420,
              flexWrap: "nowrap", // força tudo em uma linha
              // overflowX removido para não ter barra de rolagem
            }}
          >
            <span className="mr-1 text-sm whitespace-nowrap">Linhas por página</span>
            <SelectField
              options={[
                { value: "10", label: "10" },
                { value: "20", label: "20" },
                { value: "50", label: "50" },
                { value: "100", label: "100" },
              ]}
              value={String(pageSize)}
              onChange={(v) => {
                if (props.setPageSize) {
                  props.setPageSize(Number(Array.isArray(v) ? v[0] : v));
                  setCurrentPage(1);
                }
              }}
              className="pagination-page-size-select min-w-[72px] h-8 text-sm pr-6"
              aria-label="Itens por página"
              data-testid="pagination-page-size"
              containerProps={{ style: { display: "inline-block" } }}
            />
            <span className="mx-2 text-sm select-none whitespace-nowrap">
              {currentPage} de {totalPages}
            </span>
            <Button
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
              title="Primeira página"
              size="tiny"
              noBorder
              transparent
              icon={<FiChevronsLeft />}
              ariaLabel="Primeira página"
              buttonClassName="!p-0"
              data-testid="pagination-first"
            />
            <Button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              title="Página anterior"
              size="tiny"
              noBorder
              transparent
              icon={<FiChevronLeft />}
              ariaLabel="Página anterior"
              buttonClassName="!p-0"
              data-testid="pagination-prev"
            />
            <Button
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              title="Próxima página"
              size="tiny"
              noBorder
              transparent
              icon={<FiChevronRight />}
              ariaLabel="Próxima página"
              buttonClassName="!p-0"
              data-testid="pagination-next"
            />
            <Button
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
              title="Última página"
              size="tiny"
              noBorder
              transparent
              icon={<FiChevronsRight />}
              ariaLabel="Última página"
              buttonClassName="!p-0"
              data-testid="pagination-last"
            />
          </div>
        </div>
      </div>
      <DragOverlay>
        {activeId != null && activeColIdx != null && (
          <SortableTh
            col={activeId}
            colIdx={activeColIdx}
            sortCol={sortCol}
            sortDir={sortDir}
            hoverCol={hoverCol}
            setHoverCol={() => {}} // Não precisa de hover no overlay
            toggleSort={() => {}} // Não precisa de sort no overlay
            sortedData={sortedData}
            setMathMenuCol={() => {}} // Não precisa de math no overlay
            activeId={activeId}
            handleResize={() => {}} // Não precisa de resize no overlay
            allColsLength={allCols.length}
            theme={theme}
            className={clsx(
              "shadow-xl pointer-events-none", // Adicione um destaque visual
              theme === "dark" ? "bg-zinc-800" : "bg-[#f9fafb]"
            )}
            columnLabel={columnLabels[activeId] ?? undefined}
            menuCol={null}
            setMenuCol={() => {}}
            menuAnchorEl={null} // Adicionado para satisfazer a prop obrigatória
            setMenuAnchorEl={() => {}} // Adicionado para satisfazer a prop obrigatória
            onHideColumn={() => {}} // Adicionado para satisfazer a prop obrigatória
            setFilterCol={() => {}} // Adicionado para satisfazer a prop obrigatória
            setFilterModalOpen={() => {}} // Adicionado para satisfazer a prop obrigatória
            resizeGhostX={null} // Adicionado para satisfazer a prop obrigatória
            setResizeGhostX={() => {}} // Adicionado para satisfazer a prop obrigatória
          />
        )}
      </DragOverlay>
    </DndContext>
  );
}

export default Table;

// Estimativa de largura de texto (em pixels) - função simplificada
function estimateTextWidth(text: string) {
  // Aproximação: cada caractere ocupa em média 7 pixels
  return text.length * 7;
}

// TH (header) da tabela - componente sortable
function SortableTh({
  col,
  colIdx,
  sortCol,
  sortDir,
  hoverCol,
  setHoverCol,
  toggleSort,
  sortedData,
  setMathMenuCol,
  activeId,
  handleResize,
  allColsLength,
  theme,
  className,
  columnLabel,
  menuCol,
  setMenuCol,
  menuAnchorEl,
  setMenuAnchorEl,
  onHideColumn,
  setFilterCol,
  setFilterModalOpen,
  setResizeGhostX,
}: {
  col: string;
  colIdx: number;
  sortCol: string | null;
  sortDir: "asc" | "desc" | null;
  hoverCol: number | null;
  setHoverCol: (col: number | null) => void;
  toggleSort: (col: string) => void;
  sortedData: Record<string, unknown>[];
  setMathMenuCol: (col: string) => void;
  activeId: string | null;
  handleResize: (colIdx: number, delta: number) => void;
  allColsLength: number;
  theme: "light" | "dark";
  className?: string;
  columnLabel?: string;
  menuCol: string | null;
  setMenuCol: (col: string | null) => void;
  menuAnchorEl: React.RefObject<HTMLButtonElement> | null;
  setMenuAnchorEl: (ref: React.RefObject<HTMLButtonElement> | null) => void;
  onHideColumn: (col: string) => void;
  setFilterCol: (col: string) => void;
  setFilterModalOpen: (open: boolean) => void;
  resizeGhostX: number | null;
  setResizeGhostX: (x: number | null) => void;
}) {
  const { setNodeRef, listeners, isDragging, transform, transition } =
    useSortable({ id: col });

  const style = {
    ...(isDragging
      ? {
          zIndex: 1000,
          transition: "none",
          position: "absolute",
          pointerEvents: "none",
        }
      : {
          transition,
          transform: CSS.Transform.toString(transform),
        }),
  } as React.CSSProperties;

  // Fecha menu ao clicar fora
  React.useEffect(() => {
    if (!menuCol) return;
    const handler = (e: MouseEvent) => {
      if (!(e.target as HTMLElement).closest(`#col-menu-${col}`)) {
        setMenuCol(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [menuCol, setMenuCol, col]);

  const thRef = React.useRef<HTMLTableCellElement>(null);
  const buttonRef = React.useRef<HTMLButtonElement>(null);

  // Novo handler de resize com ghost
  const handleResizeStart = (e: React.MouseEvent) => {
    e.preventDefault();
    if (!thRef.current) return;
    const startX = e.clientX;
    const thRect = thRef.current.getBoundingClientRect();
    const tableContainer = thRef.current.closest('.max-w-screen');
    const containerRect = tableContainer?.getBoundingClientRect();
    const initialWidth = thRect.width;

    // Adiciona cursor global
    document.body.style.cursor = "col-resize";

    const handleMouseMove = (moveEvent: MouseEvent) => {
      const delta = moveEvent.clientX - startX;
      if (containerRect) {
        setResizeGhostX(thRect.left - containerRect.left + initialWidth + delta);
      }
    };

    const handleMouseUp = (upEvent: MouseEvent) => {
      const delta = upEvent.clientX - startX;
      handleResize(colIdx, delta);
      setResizeGhostX(null);
      // Remove cursor global
      document.body.style.cursor = "";
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    if (containerRect) {
      setResizeGhostX(thRect.left - containerRect.left + initialWidth);
    }
  };

  // Monte os itens do menu
  const isNumeric = sortedData.length > 0 && typeof sortedData[0]?.[col] === "number";
  const menuItems = [
    {
      label: "Ordenar crescente",
      icon: <FiChevronUp />,
      onClick: () => {
        if (sortCol !== col || sortDir !== "asc") toggleSort(col);
        setMenuCol(null);
      },
    },
    {
      label: "Ordenar decrescente",
      icon: <FiChevronDown />,
      onClick: () => {
        if (sortCol !== col || sortDir !== "desc") {
          toggleSort(col);
          toggleSort(col); // Chama duas vezes para inverter para desc
        }
        setMenuCol(null);
      },
    },
    ...(isNumeric
      ? [
          {
            label: "Calcular",
            icon: <MdCalculate />,
            onClick: () => {
              setMathMenuCol(col);
              setMenuCol(null);
            },
          },
        ]
      : []),
    {
      label: "Filtrar",
      icon: <FiFilter />,
      onClick: () => {
        setFilterCol(col);
        setFilterModalOpen(true);
        setMenuCol(null);
      },
    },
    {
      label: "Ocultar coluna",
      icon: <FiEyeOff />,
      onClick: () => {
        onHideColumn(col);
        setMenuCol(null);
      },
      danger: true,
    },
  ];

  return (
    <th
      ref={thRef}
      className={clsx(
        "relative select-none px-4 py-3 text-left text-sm font-semibold align-middle",
        theme === "dark"
          ? "bg-zinc-800 text-zinc-100"
          : "bg-[#f9fafb] text-gray-800",
        isDragging && "opacity-50",
        hoverCol === colIdx && "ring-2 ring-blue-400 z-30",
        activeId === col && "ring-2 ring-green-500 z-40",
        className
      )}
      style={style}
      onMouseEnter={() => setHoverCol(colIdx)}
      onMouseLeave={() => setHoverCol(null)}
    >
      <div className="flex justify-between items-center w-full min-w-0">
        <div
          className="flex-1 flex justify-center min-w-0 cursor-move"
          ref={typeof setNodeRef === "function" ? setNodeRef : undefined}
          {...(listeners || {})}
        >
          <span className={clsx("font-inter truncate", theme === "dark" ? "text-zinc-100" : "text-gray-800")}>
            {columnLabel || humanizeColumnName(col)}
          </span>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0 relative z-10">
          <Button
            type="button"
            ref={buttonRef}
            className={clsx(
              "flex items-center gap-1 focus:outline-none font-grotesk !p-0",
              theme === "dark" ? "text-zinc-100" : "text-gray-800"
            )}
            onClick={e => {
              if (e) e.stopPropagation();
              if (menuCol === col) {
                setMenuCol(null);
                setMenuAnchorEl(null);
              } else {
                setMenuCol(col);
                setMenuAnchorEl(buttonRef as React.RefObject<HTMLButtonElement>);
              }
            }}
            noBorder
            transparent
            size="tiny"
            aria-label="Mais opções"
            title="Mais opções"
            icon={<FiMoreVertical />}
            id={`col-menu-btn-${col}`}
          />
          {/* Use o Menu do projeto */}
          <Menu
            open={menuCol === col}
            anchorEl={menuAnchorEl ?? undefined}
            items={menuItems}
            onClose={() => {
              setMenuCol(null);
              setMenuAnchorEl(null);
            }}
            theme={theme}
            id={`col-menu-${col}`}
          />
        </div>
      </div>
      {colIdx < allColsLength && (
        <div
          onMouseDown={handleResizeStart}
          className="absolute top-0 right-0 h-full w-2 cursor-col-resize"
          style={{
            zIndex: 30,
            touchAction: "none",
            cursor: "col-resize",
            background: "rgba(0,0,0,0.01)",
          }}
          aria-label="Redimensionar coluna"
        />
      )}
    </th>
  );
}



/**
 * Retorna o nome da coluna em formato legível (primeira letra maiúscula, underline vira espaço).
 * @param col Nome da coluna (ex: "total_vendas")
 * @returns Nome humanizado (ex: "Total Vendas")
 */
function humanizeColumnName(col: string) {
  return col
    .replace(/_/g, " ")
    .replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.slice(1).toLowerCase());
}
